import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { adminGuard } from './core/guards/admin.guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { executiveGuard } from './core/guards/executive.guard';
import { staffGuard } from './core/guards/staff.guard';
import { accessPendingGuard, landingRedirectGuard } from './core/guards/landing.guard';
import { salesManagementGuard } from './core/guards/sales-management.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { RETURN_TO } from './core/utils/return-url';
import { MainLayoutComponent } from './shared/components/main-layout/main-layout.component';
import { AdminLayoutComponent } from './shared/components/admin-layout/admin-layout.component';

export const routes: Routes = [
  {
    path: 'login',
    title: 'pageTitle.login',
    loadComponent: () =>
      import('./features/auth/gmail-login/gmail-login.component').then(
        (m) => m.GmailLoginComponent
      ),
  },
  {
    path: 'privacy-policy',
    title: 'pageTitle.privacyPolicy',
    loadComponent: () =>
      import('./features/legal/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
  },
  {
    path: 'terms-conditions',
    title: 'pageTitle.termsAndConditions',
    loadComponent: () =>
      import('./features/legal/terms-conditions/terms-conditions.component').then(
        (m) => m.TermsConditionsComponent
      ),
  },
  {
    path: 'admin/login',
    title: 'pageTitle.adminLogin',
    loadComponent: () =>
      import('./features/auth/admin-login/admin-login.component').then(
        (m) => m.AdminLoginComponent
      ),
  },
  {
    path: 'auth/callback',
    title: 'pageTitle.authenticating',
    loadComponent: () =>
      import('./features/auth/oauth-callback/oauth-callback.component').then(
        (m) => m.OAuthCallbackComponent
      ),
  },
  {
    path: 'access-pending',
    title: 'pageTitle.accessPending',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/access-pending/access-pending.component').then(
        (m) => m.AccessPendingComponent
      ),
  },
  {
    path: 'register/:token',
    title: 'pageTitle.dataRegistration',
    loadComponent: () =>
      import('./features/trd-registration/trd-registration.component').then(
        (m) => m.TrdRegistrationComponent
      ),
  },
  {
    path: 'admin',
    data: { [RETURN_TO]: true }, // a login may return to its pages (returnableUrl)
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [adminGuard],
    children: [
      {
        path: 'dashboard',
        title: 'pageTitle.adminDashboard',
        loadComponent: () =>
          import('./features/admin/dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent
          ),
      },
      {
        path: 'users',
        title: 'pageTitle.userManagement',
        loadComponent: () =>
          import('./features/admin/users/user-list/user-list.component').then(
            (m) => m.UserListComponent
          ),
      },
      {
        path: 'users/create',
        title: 'pageTitle.createUser',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/users/user-form/user-form.component').then(
            (m) => m.UserFormComponent
          ),
      },
      {
        path: 'users/:id',
        title: 'pageTitle.userDetails',
        loadComponent: () =>
          import('./features/admin/users/user-detail/user-detail.component').then(
            (m) => m.UserDetailComponent
          ),
      },
      {
        path: 'users/:id/edit',
        title: 'pageTitle.editUser',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/users/user-form/user-form.component').then(
            (m) => m.UserFormComponent
          ),
      },
      {
        path: 'roles',
        title: 'pageTitle.roleManagement',
        loadComponent: () =>
          import('./features/admin/roles/role-list/role-list.component').then(
            (m) => m.RoleListComponent
          ),
      },
      {
        path: 'roles/create',
        title: 'pageTitle.createRole',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/roles/role-form/role-form.component').then(
            (m) => m.RoleFormComponent
          ),
      },
      {
        path: 'roles/:id',
        title: 'pageTitle.roleDetails',
        loadComponent: () =>
          import('./features/admin/roles/role-detail/role-detail.component').then(
            (m) => m.RoleDetailComponent
          ),
      },
      {
        path: 'roles/:id/edit',
        title: 'pageTitle.editRole',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/roles/role-form/role-form.component').then(
            (m) => m.RoleFormComponent
          ),
      },
      {
        path: 'audit-logs',
        title: 'pageTitle.auditLogs',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/audit-logs/audit-logs-list.component').then(
            (m) => m.AuditLogsListComponent
          ),
      },
      {
        path: 'settings',
        title: 'pageTitle.systemSettings',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/settings/system-settings-list.component').then(
            (m) => m.SystemSettingsListComponent
          ),
      },
      {
        // Who syncs their Gmail and why a mailbox stopped (card 1055 A11)
        path: 'gmail-accounts',
        title: 'pageTitle.connectedMailboxes',
        loadComponent: () =>
          import('./features/admin/connected-mailboxes/connected-mailboxes.component').then(
            (m) => m.ConnectedMailboxesComponent
          ),
      },
      {
        path: 'email-templates',
        title: 'pageTitle.emailTemplates',
        loadComponent: () =>
          import('./features/admin/email-templates/email-template-dashboard/email-template-dashboard.component').then(
            (m) => m.EmailTemplateDashboardComponent
          ),
      },
      {
        path: 'email-templates/list',
        title: 'pageTitle.emailTemplateList',
        loadComponent: () =>
          import('./features/admin/email-templates/email-template-list.component').then(
            (m) => m.EmailTemplateListComponent
          ),
      },
      {
        path: 'email-templates/create',
        title: 'pageTitle.createEmailTemplate',
        loadComponent: () =>
          import('./features/admin/email-templates/email-template-form.component').then(
            (m) => m.EmailTemplateFormComponent
          ),
      },
      {
        path: 'email-templates/:id/edit',
        title: 'pageTitle.editEmailTemplate',
        loadComponent: () =>
          import('./features/admin/email-templates/email-template-form.component').then(
            (m) => m.EmailTemplateFormComponent
          ),
      },
      {
        path: 'company-settings',
        title: 'pageTitle.companySettings',
        data: { accounting: true },
        loadComponent: () =>
          import('./features/admin/company-settings/company-settings-list.component').then(
            (m) => m.CompanySettingsListComponent
          ),
      },
      {
        path: 'company-settings/create',
        title: 'pageTitle.createCompany',
        data: { accounting: true },
        loadComponent: () =>
          import('./features/admin/company-settings/company-form/company-form.component').then(
            (m) => m.CompanyFormComponent
          ),
      },
      {
        path: 'company-settings/:id/edit',
        title: 'pageTitle.editCompany',
        data: { accounting: true },
        loadComponent: () =>
          import('./features/admin/company-settings/company-form/company-form.component').then(
            (m) => m.CompanyFormComponent
          ),
      },
      {
        path: 'document-rules',
        title: 'pageTitle.documentRules',
        loadComponent: () =>
          import('./features/admin/document-rules/document-rules-list/document-rules-list.component').then(
            (m) => m.DocumentRulesListComponent
          ),
      },
      {
        path: 'document-rules/create',
        title: 'pageTitle.createDocumentRule',
        loadComponent: () =>
          import('./features/admin/document-rules/document-rule-form/document-rule-form.component').then(
            (m) => m.DocumentRuleFormComponent
          ),
      },
      {
        path: 'document-rules/:id/edit',
        title: 'pageTitle.editDocumentRule',
        loadComponent: () =>
          import('./features/admin/document-rules/document-rule-form/document-rule-form.component').then(
            (m) => m.DocumentRuleFormComponent
          ),
      },
      {
        path: 'invoice-terms',
        title: 'pageTitle.invoiceTerms',
        data: { accounting: true },
        loadComponent: () =>
          import('./features/admin/invoice-terms/invoice-terms.component').then(
            (m) => m.InvoiceTermsComponent
          ),
      },
      {
        path: 'po-required-documents',
        title: 'pageTitle.poRequiredDocuments',
        loadComponent: () =>
          import('./features/admin/po-required-documents/po-required-documents.component').then(
            (m) => m.PoRequiredDocumentsComponent
          ),
      },
      {
        path: 'product-attributes',
        title: 'pageTitle.productAttributeManager',
        loadComponent: () =>
          import('./features/admin/product-attributes/product-attribute-manager.component').then(
            (m) => m.ProductAttributeManagerComponent
          ),
      },
      {
        path: 'ports',
        title: 'pageTitle.portManagement',
        loadComponent: () =>
          import('./features/admin/ports/port-list.component').then((m) => m.PortListComponent),
      },
      // Debt Collection
      {
        path: 'debt-collection/lawyers',
        title: 'pageTitle.lawyers',
        data: { financeOnly: true },
        loadComponent: () =>
          import('./features/admin/debt-collection/lawyers/lawyer-list/lawyer-list.component').then(
            (m) => m.LawyerListComponent
          ),
      },
      {
        path: 'debt-collection/lawyers/create',
        title: 'pageTitle.addLawyer',
        data: { financeOnly: true },
        loadComponent: () =>
          import('./features/admin/debt-collection/lawyers/lawyer-form/lawyer-form.component').then(
            (m) => m.LawyerFormComponent
          ),
      },
      {
        path: 'debt-collection/lawyers/:id/edit',
        title: 'pageTitle.editLawyer',
        data: { financeOnly: true },
        loadComponent: () =>
          import('./features/admin/debt-collection/lawyers/lawyer-form/lawyer-form.component').then(
            (m) => m.LawyerFormComponent
          ),
      },
      {
        path: 'debt-collection/cases',
        title: 'pageTitle.debtCases',
        data: { financeOnly: true },
        loadComponent: () =>
          import('./features/admin/debt-collection/cases/case-list/case-list.component').then(
            (m) => m.CaseListComponent
          ),
      },
      {
        path: 'debt-collection/cases/create',
        title: 'pageTitle.newDebtCase',
        data: { financeOnly: true },
        loadComponent: () =>
          import('./features/admin/debt-collection/cases/case-create/case-create.component').then(
            (m) => m.CaseCreateComponent
          ),
      },
      {
        path: 'debt-collection/cases/:id',
        title: 'pageTitle.caseDetails',
        data: { financeOnly: true },
        loadComponent: () =>
          import('./features/admin/debt-collection/cases/case-detail/case-detail.component').then(
            (m) => m.CaseDetailComponent
          ),
      },
      {
        // The own profile, inside the admin shell the admin is in
        path: 'profile',
        title: 'pageTitle.myProfile',
        loadComponent: () =>
          import('./features/user/user-profile.component').then((m) => m.UserProfileComponent),
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '',
    data: { [RETURN_TO]: true }, // a login may return to its pages (returnableUrl)
    component: MainLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [accessPendingGuard],
    children: [
      // The old dashboard page is retired: old links open the user's landing page
      { path: 'dashboard', canActivate: [landingRedirectGuard], children: [] },
      {
        path: 'command-center',
        title: 'pageTitle.commandCenter',
        canActivate: [staffGuard],
        loadChildren: () =>
          import('./features/command-dashboard/command-dashboard.routes').then(
            (m) => m.COMMAND_DASHBOARD_ROUTES
          ),
      },
      // Static mockups with made-up orders and money: hidden until real data exists
      { path: 'executive-overview', canActivate: [landingRedirectGuard], children: [] },
      { path: 'daily-actions', canActivate: [landingRedirectGuard], children: [] },
      {
        path: 'orders',
        pathMatch: 'full',
        title: 'pageTitle.orders',
        loadComponent: () =>
          import('./features/orders/orders-list/orders-list.component').then(
            (m) => m.OrdersListComponent
          ),
      },
      {
        path: 'orders/packages/:id/tracking',
        title: 'pageTitle.packageTracking',
        loadComponent: () =>
          import('./features/orders/package-tracking/package-tracking.component').then(
            (m) => m.PackageTrackingComponent
          ),
      },
      {
        // The document viewer lives in the order timeline.
        path: 'orders/:orderId/documents/:documentId/review',
        redirectTo: ({ params }) =>
          `/orders/timeline?order=${params['orderId']}&openDocuments=true&openDocumentId=${params['documentId']}`,
        pathMatch: 'full',
      },
      {
        path: 'orders/create',
        title: 'pageTitle.createOrder',
        loadComponent: () =>
          import('./features/orders/order-create/order-create.component').then(
            (m) => m.OrderCreateComponent
          ),
      },
      {
        path: 'orders/:orderId/edit',
        title: 'pageTitle.editOrder',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/orders/order-edit/order-edit.component').then(
            (m) => m.OrderEditComponent
          ),
      },
      {
        path: 'orders/timeline',
        title: 'pageTitle.orderTimeline',
        loadComponent: () =>
          import('./features/orders/order-timeline/order-timeline.component').then(
            (m) => m.OrderTimelineComponent
          ),
      },
      {
        path: 'orders/pipeline',
        title: 'pageTitle.orderPipeline',
        loadComponent: () =>
          import('./features/orders/order-pipeline-board/order-pipeline-board.component').then(
            (m) => m.OrderPipelineBoardComponent
          ),
      },
      {
        path: 'orders/:orderId',
        redirectTo: ({ params }) => `/orders/timeline?order=${params['orderId']}`,
        pathMatch: 'full',
      },
      {
        path: 'documents',
        title: 'pageTitle.documents',
        loadComponent: () =>
          import('./features/documents/document-dashboard/document-dashboard.component').then(
            (m) => m.DocumentDashboardComponent
          ),
      },
      {
        path: 'documents/verification',
        title: 'pageTitle.documentVerification',
        loadComponent: () =>
          import('./features/documents/document-verification/document-verification.component').then(
            (m) => m.DocumentVerificationComponent
          ),
      },
      {
        path: 'documents/generate-proforma',
        title: 'pageTitle.generateProformaInvoice',
        loadComponent: () =>
          import('./features/documents/proforma-generation/proforma-generation.component').then(
            (m) => m.ProformaGenerationComponent
          ),
      },
      {
        path: 'documents/generate-commercial-invoice',
        title: 'pageTitle.generateCommercialInvoice',
        loadComponent: () =>
          import('./features/documents/commercial-invoice-generation/commercial-invoice-generation.component').then(
            (m) => m.CommercialInvoiceGenerationComponent
          ),
      },
      {
        path: 'documents/generate-packing-list',
        title: 'pageTitle.generatePackingList',
        loadComponent: () =>
          import('./features/documents/packing-list-generation/packing-list-generation.component').then(
            (m) => m.PackingListGenerationComponent
          ),
      },
      {
        path: 'documents/generate-bill-of-lading',
        title: 'pageTitle.generateBillOfLading',
        loadComponent: () =>
          import('./features/documents/bill-of-lading-generation/bill-of-lading-generation.component').then(
            (m) => m.BillOfLadingGenerationComponent
          ),
      },
      {
        path: 'documents/quantity-variation',
        title: 'pageTitle.quantityVariation',
        loadComponent: () =>
          import('./features/documents/quantity-variation/quantity-variation.component').then(
            (m) => m.QuantityVariationComponent
          ),
      },
      {
        path: 'notifications',
        title: 'pageTitle.notificationCenter',
        loadComponent: () =>
          import('./features/notifications/notification-center/notification-center.component').then(
            (m) => m.NotificationCenterComponent
          ),
      },
      {
        path: 'documents/alerts',
        redirectTo: '/notifications',
        pathMatch: 'full',
      },
      {
        path: 'documents/status-board',
        title: 'pageTitle.documentStatusBoard',
        loadComponent: () =>
          import('./features/documents/document-status-board/document-status-board.component').then(
            (m) => m.DocumentStatusBoardComponent
          ),
      },
      {
        path: 'emails',
        title: 'pageTitle.emailHub',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/emails/email-hub/email-hub.component').then(
            (m) => m.EmailHubComponent
          ),
      },
      {
        path: 'products',
        title: 'pageTitle.products',
        loadComponent: () =>
          import('./features/admin/products/product-list/product-list.component').then(
            (m) => m.ProductListComponent
          ),
      },
      {
        path: 'suppliers',
        title: 'pageTitle.suppliers',
        // Every row opens a supplier page, which is staff only
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/suppliers/supplier-list/supplier-list.component').then(
            (m) => m.SupplierListComponent
          ),
      },
      {
        path: 'suppliers/create',
        title: 'pageTitle.addSupplier',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/suppliers/supplier-edit/supplier-edit.component').then(
            (m) => m.SupplierEditComponent
          ),
      },
      {
        path: 'suppliers/:id/edit',
        title: 'pageTitle.editSupplier',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/suppliers/supplier-edit/supplier-edit.component').then(
            (m) => m.SupplierEditComponent
          ),
      },
      {
        path: 'suppliers/:id',
        title: 'pageTitle.supplierDetails',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/suppliers/supplier-detail/supplier-detail.component').then(
            (m) => m.SupplierDetailComponent
          ),
      },
      {
        path: 'orders/:orderId/suppliers/matching',
        title: 'pageTitle.supplierMatching',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/suppliers/supplier-matching/supplier-matching.component').then(
            (m) => m.SupplierMatchingComponent
          ),
      },
      {
        path: 'leads',
        title: 'pageTitle.leadPipeline',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/leads/lead-dashboard/lead-dashboard.component').then(
            (m) => m.LeadDashboardComponent
          ),
      },
      {
        path: 'leads/create',
        title: 'pageTitle.newLead',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/leads/lead-create/lead-create.component').then(
            (m) => m.LeadCreateComponent
          ),
      },
      {
        path: 'leads/:id/edit',
        title: 'pageTitle.editLead',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/leads/lead-create/lead-create.component').then(
            (m) => m.LeadCreateComponent
          ),
      },
      {
        path: 'leads/:id',
        title: 'pageTitle.leadDetails',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/leads/lead-detail/lead-detail.component').then(
            (m) => m.LeadDetailComponent
          ),
      },
      {
        path: 'customers',
        title: 'pageTitle.customerDashboard',
        loadComponent: () =>
          import('./features/customers/customer-dashboard/customer-dashboard.component').then(
            (m) => m.CustomerDashboardComponent
          ),
      },
      {
        path: 'customers/list',
        title: 'pageTitle.customersList',
        loadComponent: () =>
          import('./features/customers/customer-list/customer-list.component').then(
            (m) => m.CustomerListComponent
          ),
      },
      {
        path: 'customers/create',
        title: 'pageTitle.addCustomer',
        loadComponent: () =>
          import('./features/customers/customer-edit/customer-edit.component').then(
            (m) => m.CustomerEditComponent
          ),
      },
      {
        path: 'customers/:id/edit',
        title: 'pageTitle.editCustomer',
        loadComponent: () =>
          import('./features/customers/customer-edit/customer-edit.component').then(
            (m) => m.CustomerEditComponent
          ),
      },
      {
        path: 'customers/:id',
        title: 'pageTitle.customerDetails',
        loadComponent: () =>
          import('./features/customers/customer-detail/customer-detail.component').then(
            (m) => m.CustomerDetailComponent
          ),
      },
      {
        path: 'qte',
        title: 'pageTitle.quoteTracking',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/qte/qte-tracking/qte-tracking.component').then(
            (m) => m.QteTrackingComponent
          ),
      },
      {
        path: 'qte/create',
        title: 'pageTitle.createQuoteRequest',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/qte/qte-management/qte-management.component').then(
            (m) => m.QteManagementComponent
          ),
      },
      {
        path: 'orders/:orderId/qte/create',
        title: 'pageTitle.createQuoteRequest',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/qte/qte-management/qte-management.component').then(
            (m) => m.QteManagementComponent
          ),
      },
      {
        path: 'qte/:qteId',
        title: 'pageTitle.quoteDetails',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/qte/qte-detail/qte-detail.component').then(
            (m) => m.QteDetailComponent
          ),
      },
      {
        path: 'qte/:qteId/quotes',
        title: 'pageTitle.quoteComparison',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/quotes/quote-comparison/quote-comparison.component').then(
            (m) => m.QuoteComparisonComponent
          ),
      },
      {
        path: 'negotiations',
        title: 'pageTitle.negotiations',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/negotiations/negotiation-list/negotiation-list.component').then(
            (m) => m.NegotiationListComponent
          ),
      },
      {
        path: 'negotiations/new',
        title: 'pageTitle.newNegotiation',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/negotiations/negotiation-create/negotiation-create.component').then(
            (m) => m.NegotiationCreateComponent
          ),
      },
      {
        path: 'negotiations/:id',
        title: 'pageTitle.negotiationDetails',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/negotiations/negotiation-detail/negotiation-detail.component').then(
            (m) => m.NegotiationDetailComponent
          ),
      },
      {
        path: 'realtime-activity',
        title: 'pageTitle.realTimeActivity',
        loadComponent: () =>
          import('./features/realtime-activity/realtime-activity.component').then(
            (m) => m.RealtimeActivityComponent
          ),
      },
      {
        path: 'analytics',
        title: 'pageTitle.analytics',
        canActivate: [staffGuard],
        loadComponent: () =>
          import('./features/analytics/analytics.component').then((m) => m.AnalyticsComponent),
      },
      {
        path: 'sales-management',
        title: 'pageTitle.salesManagement',
        canActivate: [salesManagementGuard],
        loadComponent: () =>
          import('./features/sales-management/sales-management.component').then(
            (m) => m.SalesManagementComponent
          ),
      },
      // A short address to tell people: /ceo opens the CEO dashboard
      { path: 'ceo', redirectTo: 'executive-dashboard', pathMatch: 'full' },
      {
        path: 'executive-dashboard',
        title: 'pageTitle.executiveDashboard',
        canActivate: [executiveGuard],
        loadComponent: () =>
          import('./features/executive-dashboard/executive-dashboard.component').then(
            (m) => m.ExecutiveDashboardComponent
          ),
      },
      {
        path: 'profile',
        title: 'pageTitle.myProfile',
        data: { openWithoutRole: true },
        loadComponent: () =>
          import('./features/user/user-profile.component').then((m) => m.UserProfileComponent),
      },
      {
        path: '',
        redirectTo: 'executive-dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    // An unknown address: a signed-in user goes to their own home, not back to the login page
    path: '**',
    canActivate: [authGuard],
    // One after the other: the landing page is read only for a signed-in user
    children: [{ path: '**', canActivate: [landingRedirectGuard], children: [] }],
  },
];
